import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Modal,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  MAX_READABLE_DOCUMENT_BYTES,
  MAX_READABLE_IMAGE_BYTES,
  READABLE_DOCUMENT_EXTENSIONS,
  READABLE_IMAGE_EXTENSIONS,
  classifyAttachment,
  formatFileSize,
} from "./attachmentReadability";

interface AttachFileConfirmModalProps {
  file: File | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const extensionList = (extensions: readonly string[]): string =>
  extensions.map((extension) => `.${extension}`).join(", ");

const AttachFileConfirmModal: React.FC<AttachFileConfirmModalProps> = ({
  file,
  onConfirm,
  onCancel,
}) => {
  const [acknowledged, setAcknowledged] = useState(false);

  // Re-prompt from scratch for every file, so an acknowledgement never carries over.
  useEffect(() => setAcknowledged(false), [file]);

  if (!file) return null;

  const fileName = file.name.split(/[/\\]/).pop() || file.name;
  const readability = classifyAttachment(file);
  let unreadableReason = "";
  if (!readability.readable) {
    unreadableReason =
      readability.reason === "too-large"
        ? `${fileName} is ${formatFileSize(file.size, true)}, larger than the ${formatFileSize(readability.limitBytes)} the assistant can read.`
        : `The assistant can't read ${readability.extension ? `.${readability.extension}` : "this type of"} files.`;
  }

  return (
    <Modal
      visible={true}
      onDismiss={onCancel}
      header="Attach file to this conversation"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              onClick={onCancel}
              data-testid="attach-file-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={onConfirm}
              disabled={!acknowledged}
              data-testid="attach-file-confirm"
            >
              Attach file
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween direction="vertical" size="m">
        {readability.readable ? (
          <Box>
            The assistant can read <b>{fileName}</b> (
            {formatFileSize(file.size)}) in this conversation. You can also ask
            it to add the file to an existing audit, notice, litigation, or
            task.
          </Box>
        ) : (
          <Alert
            type="warning"
            header="The assistant won't be able to read this file"
          >
            {unreadableReason} It won&apos;t see the contents in this
            conversation, but it can still act on the file for you — for
            example, you can ask it to add the file to an existing audit,
            notice, litigation, or task.
          </Alert>
        )}

        <Box variant="small" color="text-body-secondary">
          The assistant can read documents (
          {extensionList(READABLE_DOCUMENT_EXTENSIONS)}) up to{" "}
          {formatFileSize(MAX_READABLE_DOCUMENT_BYTES)} and images (
          {extensionList(READABLE_IMAGE_EXTENSIONS)}) up to{" "}
          {formatFileSize(MAX_READABLE_IMAGE_BYTES)}.
        </Box>

        <Box variant="small" color="text-body-secondary">
          It reads attachments with AI and can misread tables, figures, or
          handwriting. Verify anything you rely on against the source file.
        </Box>

        <Checkbox
          checked={acknowledged}
          onChange={({ detail }) => setAcknowledged(detail.checked)}
          data-testid="attach-file-acknowledge"
        >
          {readability.readable
            ? "I understand the assistant reads this file with AI and may make mistakes."
            : "I understand the assistant won't be able to read this file's contents."}
        </Checkbox>
      </SpaceBetween>
    </Modal>
  );
};

export default AttachFileConfirmModal;
